import React, { ReactNode } from 'react';
import { cn } from '../../../../lib/utils';
import { DeleteColumnConfirm } from '../ColumnMenu';
import { TaskColumn } from '../../../../types/topic';

interface TaskBoardLayoutProps {
  containerRef: React.RefObject<HTMLDivElement>;
  boardRef: React.RefObject<HTMLDivElement>;
  shouldScroll: boolean;
  measurementComplete: boolean;
  columnWidth: string | null;
  children: ReactNode;
  columns: TaskColumn[];
  columnToDelete: string | null;
  onDeleteColumn: (columnId: string | null) => void;
  onCancelDelete: () => void;
}

export const TaskBoardLayout: React.FC<TaskBoardLayoutProps> = ({
  containerRef,
  boardRef,
  shouldScroll,
  measurementComplete,
  columnWidth,
  children,
  columns,
  columnToDelete,
  onDeleteColumn,
  onCancelDelete,
}) => {
  // Don't render anything until measurements are complete
  if (!measurementComplete || !columnWidth) {
    return <div className="h-full w-full bg-gray-50" ref={containerRef}></div>;
  }

  return (
    <>
      {/* Main board container with proper overflow handling */}
      <div ref={containerRef} className="h-full w-full overflow-hidden relative">
        {/* Horizontal scrolling container for columns */}
        <div
          ref={boardRef}
          className={cn(
            'h-full w-full',
            shouldScroll
              ? 'overflow-x-auto overflow-y-hidden'
              : 'overflow-x-hidden overflow-y-hidden'
          )}
          style={{
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            scrollbarColor: '#CBD5E0 #F7FAFC',
          }}
        >
          {/* Inner content container that expands with columns */}
          <div
            className="h-full flex gap-4 pt-0 pb-6 px-4 w-full"
            style={{
              minWidth: shouldScroll ? 'max-content' : '100%',
              willChange: 'transform', // Hint to browser to use GPU for this element
              contain: 'layout', // Isolate layout changes to this container
            }}
          >
            {children}
          </div>
        </div>
      </div>

      {/* Delete column confirmation */}
      <DeleteColumnConfirm
        columnId={columnToDelete}
        columns={columns}
        onConfirm={() => onDeleteColumn(columnToDelete)}
        onCancel={onCancelDelete}
      />
    </>
  );
};
